<template>
    <v-container class="activation">
        <v-card class="activation-card text-center">
            <div class="activation-icon">
                <v-icon color="white" size="34">fal fa-envelope-open-text</v-icon>
            </div>

            <h1 class="activation-title">{{ $t('Aktivace uživatelského účtu') }}</h1>

            <p v-if="emailKnown" class="activation-text">
                {{ $t('Poslali jsme 8místný aktivační kód na adresu') }}
            </p>
            <div v-if="emailKnown" class="activation-email">{{ email }}</div>
            <p v-if="emailKnown" class="activation-text activation-text--second">
                {{ $t('Opište ho prosím do pole níže, tím se Vám účet aktivuje.') }}
            </p>
            <p v-else class="activation-text">
                {{ $t('Na zadaný e-mail jsme poslali 8místný aktivační kód. Opište ho prosím do pole níže, tím se Vám účet aktivuje.') }}
            </p>

            <CodeField
                ref="codeField"
                v-model="code"
                placeholder="12345678"
                :error-messages="errorMessage"
                :disabled="activating"
                class="activation-code"
                @complete="activate()"
                @enter="activate()"
            ></CodeField>

            <v-btn block color="green" dark x-large class="activation-submit font-weight-bold"
                   :loading="activating"
                   @click="activate()">
                {{ $t('Aktivovat') }}
            </v-btn>

            <div class="activation-resend">
                <template v-if="!showResend">
                    <a class="resend-link" @click="showResend = true">{{ $t('Kód nedorazil? Poslat nový') }}</a>
                </template>
                <template v-else>
                    <v-text-field
                        v-if="!emailKnown"
                        v-model="email"
                        label="E-mail"
                        type="email"
                        autocomplete="email"
                        outlined
                        dense
                        :error-messages="resendError"
                        :disabled="resending"
                        @keyup.enter="resend()"
                    ></v-text-field>
                    <v-btn text color="green" :loading="resending" @click="resend()">
                        {{ $t('Poslat kód znovu') }}
                    </v-btn>
                    <p v-if="emailKnown && resendError" class="activation-feedback activation-feedback--error">{{ resendError }}</p>
                    <p v-if="resendInfo" class="activation-feedback">{{ resendInfo }}</p>
                </template>
            </div>
        </v-card>
    </v-container>
</template>

<script>
import CodeField from "@/components/auth/CodeField.vue";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
    components: { CodeField },

    data() {
        return {
            code: "",
            activating: false,
            errorMessage: "",

            // The e-mail is known right after registering or logging in; after a restart of the app it has to be typed again (and stays editable).
            email: (this.$route.params && this.$route.params.email) || "",
            // A known e-mail is the one from the registration/login and must not be changed here.
            emailKnown: !!(this.$route.params && this.$route.params.email),
            // Coming from a login attempt, the person most likely lost the code: offer a new one right away.
            showResend: !!(this.$route.params && this.$route.params.fromLogin),
            resending: false,
            resendError: "",
            resendInfo: "",
        };
    },

    watch: {
        code() {
            this.errorMessage = "";
        },
        email() {
            this.resendError = "";
            this.resendInfo = "";
        },
    },

    methods: {
        translate(message) {
            return this.$te(message) ? this.$t(message) : message;
        },

        async activate() {
            if (this.activating) return;

            if (this.code.length !== 8) {
                this.errorMessage = this.$t("Kód má 8 číslic. Opište ho prosím z e-mailu.");
                return;
            }

            this.activating = true;
            const result = await this.$store.dispatch("activate", this.code);
            this.activating = false;

            if (!result.ok) {
                this.errorMessage = this.translate(result.message);
                return;
            }

            this.$root.$emit("notification", {
                message: this.$t("Aktivace proběhla úspěšně. Nyní se můžete přihlásit."),
                type: "success",
                duration: 6,
            });
            this.$router.push({ name: "Home" });
            this.$root.$emit("openLoginDialog");
        },

        async resend() {
            if (this.resending) return;

            this.email = (this.email || "").trim();

            if (!EMAIL_PATTERN.test(this.email)) {
                this.resendError = this.$t("Zadejte prosím platnou e-mailovou adresu.");
                return;
            }

            this.resending = true;
            const result = await this.$store.dispatch("resendActivation", this.email);
            this.resending = false;

            if (!result.ok) {
                this.resendError = this.translate(result.message);
                return;
            }

            this.resendInfo = this.$t("Pokud účet čeká na aktivaci, poslali jsme na e-mail nový kód.");
        },
    },

    mounted() {
        window.history.replaceState(null, '', '/');
        this.$nextTick(() => this.$refs.codeField && this.$refs.codeField.focus());
    },
};
</script>

<style>
.activation {
    max-width: 440px;
}

.activation-card {
    padding: 36px 24px 28px;
    border-radius: 16px !important;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.activation-icon {
    width: 72px;
    height: 72px;
    /* Vuetify rounds only the top corners of a card's first child, so force the full circle. */
    border-radius: 50% !important;
    background: #91A913;
    box-shadow: 0 0 0 8px rgba(145, 169, 19, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
}

.activation-title {
    font-size: 1.6rem;
    line-height: 1.25;
    font-weight: 800;
    color: #000;
    margin-bottom: 14px;
}

.activation-text {
    font-size: 1rem;
    line-height: 1.5;
    color: rgba(0, 0, 0, 0.7);
    margin: 0;
}

.activation-text--second {
    margin-top: 2px;
}

.activation-email {
    margin: 8px 0;
    padding: 6px 14px;
    max-width: 100%;
    border-radius: 999px;
    background: rgba(145, 169, 19, 0.12);
    color: #000;
    font-weight: 700;
    font-size: 1.05rem;
    overflow-wrap: anywhere;
}

.activation-code {
    width: 100%;
    max-width: 280px;
    margin-top: 26px;
}

.activation-submit {
    margin-top: 4px;
    font-weight: 900 !important;
    border-radius: 12px !important;
}

.activation-resend {
    width: 100%;
    margin-top: 22px;
}

.activation-feedback {
    margin: 8px 0 0;
    color: #2e7d32;
}

.activation-feedback--error {
    color: #b00020;
}

.activation .resend-link {
    color: #91A913 !important;
    text-decoration: underline;
    cursor: pointer;
}
</style>
